import type { ReactNode } from "react";
import { Globe, Gauge, Factory, Sun, Users, Award, Link2, HeartHandshake, GraduationCap, ClipboardCheck, BookOpen, Flame, Droplet } from "lucide-react";
import { Reveal } from "./lib";
import { type Highlight } from "./content";

const ICONS: Record<string, typeof Globe> = {
  globe: Globe, gauge: Gauge, factory: Factory, sun: Sun, users: Users, award: Award, link: Link2,
  heart: HeartHandshake, graduation: GraduationCap, clipboard: ClipboardCheck, book: BookOpen, flame: Flame, droplet: Droplet,
};

export function HighlightItem({ item, index, testId }: { item: Highlight; index: number; testId: string }) {
  const Icon = ICONS[item.icon] ?? Globe;
  return (
    <Reveal as="li" delay={(index % 4) * 0.06} className="flex gap-4" testId={testId}>
      <span className="mt-0.5 shrink-0 am-accent">
        <Icon size={34} strokeWidth={1.1} aria-hidden />
      </span>
      <p className="text-[15px] leading-relaxed">
        <span className="am-hl-lead">{item.lead}</span>
        {item.rest}
      </p>
    </Reveal>
  );
}

export function HighlightsPanel({ items, page, prefix }: { items: Highlight[]; page: number; prefix: string }) {
  return (
    <aside className="bg-[var(--am-panel)] p-6 sm:p-8" data-testid={`${prefix}-highlights`}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-xl font-semibold am-accent">2025 Highlights</h3>
      </div>
      <ul className="space-y-6">
        {items.map((h, i) => (
          <HighlightItem key={h.lead} item={h} index={i} testId={`${prefix}-highlight-${i}`} />
        ))}
      </ul>
    </aside>
  );
}

export function ChartCard({ title, subtitle, page, testId, children, table }: { title: string; subtitle?: string; page: number; testId: string; children: ReactNode; table: ReactNode }) {
  return (
    <Reveal className="flex flex-col rounded-sm border border-[var(--am-line)] bg-white p-5 sm:p-7" testId={testId}>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-lg font-semibold text-[#2b2d30]">{title}</h4>
          {subtitle && <p className="mt-1 text-sm">{subtitle}</p>}
        </div>
      </div>
      <div className="flex-1">{children}</div>
      <details className="mt-5 border-t border-[var(--am-line)] pt-3 text-sm" data-testid={`${testId}-table-toggle`}>
        <summary className="cursor-pointer font-semibold am-accent">View data table</summary>
        <div className="mt-3 overflow-x-auto">{table}</div>
      </details>
    </Reveal>
  );
}

export function SimpleTable({ caption, head, rows }: { caption: string; head: string[]; rows: (string | number)[][] }) {
  return (
    <table className="w-full min-w-[320px] text-left text-sm">
      <caption className="sr-only">{caption}</caption>
      <thead>
        <tr>{head.map((h) => <th key={h} scope="col" className="border-b border-[var(--am-line)] py-2 pr-3 font-semibold text-[#2b2d30]">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={String(r[0])}>{r.map((c, i) => <td key={i} className="border-b border-[var(--am-line)] py-2 pr-3 tabular-nums">{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
}
